import React, { useState, useEffect } from 'react';
import { Building2, Sparkles, CheckCircle2 } from 'lucide-react';

export const EmployeeWelcomeTransition = ({ user, onComplete, branding = {} }) => {
  const [stage, setStage] = useState(0); // 0: Logo & Card Reveal, 1: Identity & Avatar, 2: Role & Company, 3: Portal Ready, 4: Exit
  const [reducedMotion, setReducedMotion] = useState(false);

  // Check user preference for reduced motion
  useEffect(() => {
    try {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setReducedMotion(mediaQuery.matches);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      // Fast simple fade for reduced motion preference (~500ms)
      const t1 = setTimeout(() => setStage(3), 300);
      const t2 = setTimeout(() => {
        setStage(4);
        setTimeout(onComplete, 200);
      }, 600);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }

    // High-speed, ultra-responsive enterprise sequence (~1.1 seconds total)
    const t0 = setTimeout(() => setStage(1), 200);  // Name & Avatar appear
    const t1 = setTimeout(() => setStage(2), 500);  // Role & Company details appear
    const t2 = setTimeout(() => setStage(3), 850);  // Workspace ready indicator fills
    const t3 = setTimeout(() => {
      setStage(4);                                 // Exit fade & backdrop expand
      setTimeout(onComplete, 250);                 // Trigger immediate dashboard reveal
    }, 1150);

    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [onComplete, reducedMotion]);

  // Extract identity details dynamically from authenticated user context
  const primaryColor = branding.primary_color || '#0052FF';
  const accentColor = branding.accent_color || '#10B981';
  const logoUrl = branding.logo_url || 'https://hecs.in/storage/logo/hecs-logo-1.png';
  const appName = branding.app_name || 'PayrollPro';

  const name = (user?.name || user?.email || 'Employee').trim();
  const rawRole = (user?.role || 'employee').replace(/_/g, ' ');
  const formattedRole = rawRole.charAt(0).toUpperCase() + rawRole.slice(1);
  const empCode = user?.employee_code || user?.emp_code || user?.code || '';
  const companyName = user?.company_name || user?.company?.name || user?.tenant_name || 'HECS Ltd';
  const branchName = user?.branch_name || user?.location || user?.work_location || '';
  const profilePhoto = user?.profile_photo || user?.avatar_url || null;

  // Compute initials for fallback avatar
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase() || 'EP';

  return (
    <div
      data-testid="welcome-transition-overlay"
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        stage === 4 ? 'opacity-0 scale-102 pointer-events-none' : 'opacity-100 scale-100'
      }`}
      style={{
        background: 'radial-gradient(circle at center, #FFFFFF 0%, #F1F5F9 100%)',
      }}
    >
      {/* Ambient background glow elements */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/2 -translate-x-1/2 translate-y-1/2 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Glass Welcome Card Container */}
      <div className="relative w-full max-w-md bg-white/80 backdrop-blur-xl border border-slate-200/80 rounded-2xl p-8 shadow-2xl shadow-slate-900/10 text-center space-y-6 flex flex-col items-center overflow-hidden">
        
        {/* Top Brand Accent Line */}
        <div 
          className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-indigo-500 to-emerald-500" 
        />

        {/* Stage 0: Brand Logo Reveal */}
        <div
          className={`transition-all duration-400 transform ${
            stage >= 0 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <div className="relative inline-block p-3 rounded-xl bg-white shadow-sm border border-slate-100">
            <img
              src={logoUrl}
              alt={appName}
              className="w-12 h-12 object-contain mx-auto drop-shadow-sm"
            />
          </div>
        </div>

        {/* Stage 1: Employee Avatar & Welcome Back */}
        <div
          className={`space-y-3 transition-all duration-400 transform ${
            stage >= 1 ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-2 scale-95'
          }`}
        >
          {/* Avatar with Subtle Ring Glow */}
          <div className="relative inline-block my-1">
            {profilePhoto ? (
              <img
                src={profilePhoto}
                alt={name}
                className="w-20 h-20 rounded-full object-cover shadow-lg border-2 border-white ring-4 ring-blue-500/20 mx-auto"
              />
            ) : (
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg border-2 border-white ring-4 ring-blue-500/20 mx-auto transition-transform duration-300"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor}, #3B82F6)`,
                }}
              >
                {initials}
              </div>
            )}
            <div 
              className="absolute bottom-0 right-0 p-1 bg-emerald-500 rounded-full border-2 border-white text-white shadow-sm"
              style={{ background: accentColor }}
            >
              <Sparkles className="w-3.5 h-3.5" />
            </div>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
              Welcome back
            </p>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-1">
              {name}
            </h1>
          </div>
        </div>

        {/* Stage 2: Role, Employee Code & Company Information */}
        <div
          className={`space-y-1.5 transition-all duration-400 transform ${
            stage >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100/80 border border-slate-200/60 text-xs font-semibold text-slate-700">
            <span>{formattedRole}</span>
            {empCode && <span className="text-slate-400">•</span>}
            {empCode && <span className="font-mono text-slate-900">{empCode}</span>}
          </div>

          <div className="flex items-center justify-center gap-1.5 text-xs text-slate-600 font-medium pt-1">
            <Building2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
            <span>
              {companyName} {branchName ? `· ${branchName}` : ''}
            </span>
          </div>
        </div>

        {/* Stage 3: Workspace Preparation Animated Indicator */}
        <div
          className={`pt-2 w-full max-w-xs space-y-2.5 transition-all duration-400 ${
            stage >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          }`}
        >
          <div className="flex items-center justify-center gap-2 text-xs font-medium text-slate-500">
            <span>Preparing your workspace</span>
            <span className="flex gap-1.5 items-center">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-bounce" style={{ animationDelay: '300ms' }} />
            </span>
          </div>

          <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500 ease-out"
              style={{
                width: stage >= 3 ? '100%' : '25%',
                background: `linear-gradient(90deg, ${primaryColor}, ${accentColor})`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeWelcomeTransition;
